import { IBookmark } from '@/models/IBookmark'
import {
	faBookmark,
	faChevronLeft,
	faFileText,
	faLink,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import { createPortal } from 'react-dom'
import './index.scss'
import { Button } from '@/components/Button'
import { LocalStorageHelper } from '@/utils/local-storage-helper'
import { bookmarksApiService } from '@/services/bookmarks-api.services'
import { IHeader } from '@/models/IHeader'

interface ModalProps {
	isOpen: boolean
	onClose: () => void
	children: ReactNode
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
	if (!isOpen) return null

	return createPortal(
		<section className="modal bg-white overflow-auto top-0 right-0">
			<div className="overlay"></div>
			<div className="flex content py-4 text-left px-6 w-full md:w-[480px]">
				{children}
			</div>
		</section>,
		document.getElementById('modal-root') as HTMLDivElement,
	)
}

interface BookmarkProps {
	isMobile: boolean,
	backToMenu: () => void
}

export default function BookmarksModal({ isMobile, backToMenu }: BookmarkProps) {
	const [modalVisibility, setmodalVisibility] = useState<boolean>(false)
	const [bookmarks, setBookmarks] = useState<IBookmark[]>([])
	const [loading, setLoading] = useState<boolean>(false)
	const parentPages = [
		'Resources',
		'Articles',
		'Members',
		'Volunteers',
		'Events',
	]

	const closeModal = () => {
		setmodalVisibility(false)
	}

	const backToMenuHandler = () => {
		backToMenu();
		setmodalVisibility(false)
	}

	const onBookmarksOpen = async () => {
		setmodalVisibility(true)
		const header = LocalStorageHelper.getItem<IHeader>('header-info')
		if (header?.memberId) {
			const bookmarks = LocalStorageHelper.getItem<IBookmark[]>(`bookmarks-${header.memberId}`)
			if(bookmarks === undefined || bookmarks === null) {
				setLoading(true)
				setBookmarks(
					await bookmarksApiService.getAndStoreBookmarks(header.memberId),
				)
				setLoading(false)
			}
			else {
				setBookmarks(bookmarks ?? []);
			}
		}
	}

	const unBookmark = async (pageId: number) => {
		const header = LocalStorageHelper.getItem<IHeader>('header-info')
		if (header?.memberId) {
			await bookmarksApiService.deleteBookmark(
				header.memberId,
				pageId,
			)
			const bookmarks = LocalStorageHelper.getItem<IBookmark[]>(`bookmarks-${header.memberId}`)
			setBookmarks(bookmarks ?? []);
		}
	}

	useEffect(() => {
		if (modalVisibility) {
			document.body.classList.add('overflow-hidden')
		} else {
			document.body.classList.remove('overflow-hidden')
		}
	}, [modalVisibility])

	return (
		<div>
			{isMobile ? (
				<a onClick={onBookmarksOpen} className='flex gap-3'>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M7 4C6.73478 4 6.48043 4.10536 6.29289 4.29289C6.10536 4.48043 6 4.73478 6 5V19.0568L11.4188 15.1863C11.7665 14.9379 12.2335 14.9379 12.5812 15.1863L18 19.0568V5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4H7ZM4.87868 2.87868C5.44129 2.31607 6.20435 2 7 2H17C17.7956 2 18.5587 2.31607 19.1213 2.87868C19.6839 3.44129 20 4.20435 20 5V21C20 21.3746 19.7907 21.7178 19.4576 21.8892C19.1245 22.0606 18.7236 22.0315 18.4188 21.8137L12 17.2289L5.58124 21.8137C5.27642 22.0315 4.87549 22.0606 4.54242 21.8892C4.20935 21.7178 4 21.3746 4 21V5C4 4.20435 4.31607 3.44129 4.87868 2.87868Z"
							fill="#004A99"
						/>
					</svg>
					Bookmarks
				</a>
			) : (
				<button
					onClick={onBookmarksOpen}
					className="bg-sky-800 border border-sky-800 text-text-color-0 justify-center items-center gap-3 flex md:w-auto"
					data-tooltip-id="bookmark-tooltip"
					data-tooltip-content="Bookmarks"
					data-tooltip-place="bottom"
				>
					<FontAwesomeIcon
						icon={faBookmark}
						className={'w-[18px] h-[18px]'}
					/>
				</button>
			)}
			<Tooltip id="bookmark-tooltip" />
			<Modal isOpen={modalVisibility} onClose={closeModal}>
				<div className="w-full">
					<div className="flex-col">
						<button
							className="close absolute right-0 cursor-pointer p-4 top-1 mt"
							onClick={closeModal}
						>
							<svg
								className="fill-current h-6 w-6 text-black"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path d="M14.348 5.652a.5.5 0 10-.707-.707L10 9.293 5.359 4.652a.5.5 0 10-.707.707L9.293 10l-4.648 4.641a.5.5 0 00.707.707L10 10.707l4.641 4.648a.498.498 0 00.707 0 .5.5 0 000-.707L10.707 10l4.641-4.648z" />
							</svg>
						</button>

						<button onClick={backToMenuHandler} className="flex md:hidden py-5">
							<FontAwesomeIcon
								icon={faChevronLeft}
								className={
									'w-[14px] h-[14px] text-action-secondary-120 pt-1'
								}
							/>
							<span className="font-bold text-action-secondary-120 ml-2">
								BACK TO MENU
							</span>
						</button>
						<div className="flex gap-3 text-sm lg:text-lg text-action-primary-blue-100 pb-4 font-bold pointer-events-auto">
							<svg
								width="26"
								height="26"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M7 4C6.73478 4 6.48043 4.10536 6.29289 4.29289C6.10536 4.48043 6 4.73478 6 5V19.0568L11.4188 15.1863C11.7665 14.9379 12.2335 14.9379 12.5812 15.1863L18 19.0568V5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4H7ZM4.87868 2.87868C5.44129 2.31607 6.20435 2 7 2H17C17.7956 2 18.5587 2.31607 19.1213 2.87868C19.6839 3.44129 20 4.20435 20 5V21C20 21.3746 19.7907 21.7178 19.4576 21.8892C19.1245 22.0606 18.7236 22.0315 18.4188 21.8137L12 17.2289L5.58124 21.8137C5.27642 22.0315 4.87549 22.0606 4.54242 21.8892C4.20935 21.7178 4 21.3746 4 21V5C4 4.20435 4.31607 3.44129 4.87868 2.87868Z"
									fill="#004A99"
								/>
							</svg>
							<p className="font-bold text-lg">Bookmarks</p>
						</div>
						{loading && (
							<div className="text-stone-700 text-base mt-3">
								Loading...
							</div>
						)}
						{!loading &&
							bookmarks &&
							bookmarks?.map((bookmark) => (
								<span key={bookmark.id}>
									<hr className="mb-1 bg-border/40 h-0.5 border-none"></hr>
									<div className="flex gap-4 py-1">
										{parentPages.includes(
											bookmark.pageType,
										) ? (
											<button className="mb-1 p-[10px]  text-action-primary-blue-100 justify-center items-center flex ">
												<FontAwesomeIcon
													icon={faFileText}
													className={
														'w-[24px] h-[24px]'
													}
												/>
											</button>
										) : (
											<button className="mb-1 p-[10px]  text-action-primary-blue-100 justify-center items-center flex ">
												<FontAwesomeIcon
													icon={faLink}
													className={
														'w-[24px] h-[24px]'
													}
												/>
											</button>
										)}
										<div
											className="mb-4 w-2/3 text-stone-700 text-base mt-3"
											key={bookmark.id}
										>
											<a
												href={bookmark.url}
												target="_blank"
											>
												{bookmark.name}
											</a>
										</div>
										<div className="justify-center items-center flex">
											<button
												disabled={loading}
												onClick={() =>
													unBookmark(bookmark.id)
												}
												className={
													loading
														? 'p-[10px] hover:bg-sky-700 rounded-[60px] stroke-sky-800 border text-action-secondary-20 justify-center items-center flex'
														: 'p-[10px] bg-sky-800 rounded-[60px] border stroke-white text-action-secondary-20 justify-center items-center flex hover:bg-sky-700'
												}
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M7 4C6.73478 4 6.48043 4.10536 6.29289 4.29289C6.10536 4.48043 6 4.73478 6 5V19.0568L11.4188 15.1863C11.7665 14.9379 12.2335 14.9379 12.5812 15.1863L18 19.0568V5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4H7ZM4.87868 2.87868C5.44129 2.31607 6.20435 2 7 2H17C17.7956 2 18.5587 2.31607 19.1213 2.87868C19.6839 3.44129 20 4.20435 20 5V21C20 21.3746 19.7907 21.7178 19.4576 21.8892C19.1245 22.0606 18.7236 22.0315 18.4188 21.8137L12 17.2289L5.58124 21.8137C5.27642 22.0315 4.87549 22.0606 4.54242 21.8892C4.20935 21.7178 4 21.3746 4 21V5C4 4.20435 4.31607 3.44129 4.87868 2.87868Z"
														fill="#AAAAAA"
													/>
												</svg>
											</button>
										</div>
									</div>
								</span>
							))}
						{!loading && bookmarks?.length == 0 && (
							<div className="text-stone-700 text-base mt-3">
								No bookmarks
							</div>
						)}
					</div>
				</div>
			</Modal>
		</div>
	)
}
